<template>
  <template v-if="!pending">
  <GlobalDependencyProvider>
    <NuxtLayout>
      <div class="teachino-theme h-(--app-height)">
        <NuxtPage />
        <TGlobalDrawerContainer />
        <TGlobalToastContainer />
        <TGlobalModalContainer />
        <ModalV2Renderer />
      </div>
    </NuxtLayout>

  </GlobalDependencyProvider>
  </template>

  <div v-else>
    <div class="loading-container">
      <div class="center" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ModalV2Renderer,
  openFeatureFlagDialog,
  TGlobalDrawerContainer,
  TGlobalModalContainer,
  TGlobalToastContainer
} from '@teachino/shared-design-system';
import useDetermineWindowHeight from '~/composables/useDetermineWindowHeight';
import { useSession } from '~/stores/session';
import * as theme from '../../../tailwind-theme-variables.css';
import GlobalDependencyProvider
  from './components/GlobalDependencyProvider.vue';
import { onMounted, onUnmounted } from 'vue';
import { useBeta } from '@teachino/shared-util';

const _ = { theme };

const sessionStore = useSession();
sessionStore.init();

function delay(ms = 400) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function doDelay() {
  if (sessionStore.isLoggedIn) {
    await sessionStore.loadUserIfNeeded();
    const { loadUserIfNeeded } = useCurrentUser();
    await loadUserIfNeeded();
  }

  await delay(500);
}

const { pending } = useAsyncData('delayanmiation', doDelay);

const { $t } = useNuxtApp();

useDetermineWindowHeight();
const isBeta = useBeta().isBeta();

useHead({
  titleTemplate: (titleChunk) =>
    titleChunk && titleChunk != 'Teachino'
      ? `${titleChunk} | Teachino`
      : 'Teachino',
  meta: [
    {
      name: 'viewport',
      content:
        'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover',
    },
    {
      name: 'robots',
      content: 'noindex, nofollow',
    },
    {
      name: 'msapplication-TileColor',
      content: '#ffffff',
    },
    {
      name: 'msapplication-config',
      content: '/pictures/icons/browserconfig.xml',
    },
    {
      name: 'theme-color',
      content: '#ffffff',
    },
    {
      name: 'description',
      content: $t('generic_slogan'),
    },
    {
      name: 'og:title',
      content: 'Teachino',
    },
    {
      name: 'og:description',
      content: $t('generic_slogan'),
    },
    {
      name: 'og:image',
      content:
        `https://${  window.location.hostname  }/pictures/ogsharing.png`,
    },
    {
      name: 'og:image:width',
      content: '1200',
    },
    {
      name: 'og:image:height',
      content: '630',
    },
  ],
  link: [
    {
      rel: 'apple-touch-icon',
      sizes: '180x180',
      href: '/pictures/icons/apple-touch-icon.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: '/pictures/icons/favicon-32x32.png',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: '/pictures/icons/favicon-16x16.png',
    },
    {
      rel: 'manifest',
      href: '/pictures/icons/site.webmanifest',
      crossorigin: 'use-credentials',
    },
    {
      rel: 'mask-icon',
      color: '#ff8b21',
      href: '/pictures/icons/safari-pinned-tab.svg',
    },
    {
      rel: 'shortcut icon',
      href: '/favicon.ico',
      type: 'image/x-icon',
    },
  ],
  bodyAttrs: {
    class: 'antialiased',
  },
});


  const handleKeyDown = async (event: KeyboardEvent) => {
    // Check for Ctrl+Shift+F (Windows/Linux) or Cmd+Shift+F (Mac)
    const isModifierPressed = event.ctrlKey || event.metaKey;
    const isShiftPressed = event.shiftKey;
    const isF = event.key === 'F' || event.key === 'f';

    if (isModifierPressed && isShiftPressed && isF) {
      event.preventDefault();
      await openFeatureFlagDialog();
    }
  };

  onMounted(() => {
    if(isBeta) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      window.addEventListener('keydown', handleKeyDown);
    }
  });

  onUnmounted(() => {
    if(isBeta) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      window.removeEventListener('keydown', handleKeyDown);
    }
  });


</script>

<style scoped>
body,
.loading-container {
  margin: 0;
  width: 100vw;
  height: 100vh;
  padding-top: 50vh;
}

body {
  margin: 0;
  width: 100%;
  height: 100vh;
  padding-top: 50vh;
}

.center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top: -24px;
  height: 48px;
  width: 48px;
  overflow: hidden;

  animation: expandlogo 0.5s ease-in;
  animation-fill-mode: forwards;

  background-image: url('https://storage.de.cloud.ovh.net/v1/AUTH_97856089b0cd40beace6493b15fd316e/teachinomedia/images/teachino_logo.svg');

  background-repeat: no-repeat;
  background-position: left;
  background-size: cover;
}

@keyframes expandlogo {
  0% {
    /*transform: scale(1);
    opacity: 1;*/
    width: 48px;
  }

  100% {
    /*transform: scale(16);
    opacity: 0;*/
    width: 248px;
  }
}
</style>
